"use client";

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { FAQ_EN, FAQ_NL, faqDictEntries } from "./faqContent";

export type Lang = "en" | "nl";

type Dict = Record<string, string | string[]>;

const EN: Dict = {
  "seo.h1": "Plot Score (PlotScore) — Dutch real estate evaluation for homebuyers in the Netherlands. Kadaster, WOZ, energy label and zoning data for every address.",

  "nav.score": "The score",
  "nav.who": "Who it's for",
  "nav.pricing": "Pricing",
  "nav.cta": "Start free",
  "nav.menu": "Menu",
  "nav.close": "Close",
  "nav.language": "Language",

  "hero.stage1.headline": "One score for<br/>every Dutch <em>property.</em>",
  "hero.stage1.body": "Search any address. 10 million parcels, one composite score, in seconds. Three free scores — no card required.",
  "hero.stage1.cta": "Score your first property",
  "hero.stage1.secondary": "See how it works",
  "hero.stage2.label": "Pick any parcel",
  "hero.stage2.headline": "Click a building.<br/>Any building.",
  "hero.stage2.body": "Anywhere in the Netherlands. Residential, commercial, industrial — if it has a footprint, we&rsquo;ve got it.",
  "hero.score.label": "Composite Score",
  "hero.score.cta": "Score your first property",
  "hero.scroll": "Scroll to explore",

  // The five layers, annotated on the split building. Each slab first carries
  // the QUESTION a reader is already asking, with one real fact under it, and
  // turns into the layer's product name as the house turns green. Every fact
  // is read off the Laurierstraat 218-1 capture in public/product/sidebar-*.png
  // — the property the composite (HERO_COMPOSITE in heroLayers.ts) and the sub-scores belong to — never
  // typed from memory (CLAUDE.md § Marketing claims). Each description lists
  // factors that genuinely score in that dimension (src/lib/propertyScore.ts).
  // The factor count is the sitewide "90+" (see who.card1.desc), not a derived
  // number — it grows, and the two must never disagree.
  "hero.split.label": "Anatomy of a score",
  "hero.split.headlineQ": "Five questions.<br/>One number.",
  "hero.split.headline": "Five layers.<br/>One number.",
  "hero.composite.body":
    "90+ scored factors across five layers, resolved into a single number.",
  "hero.layer.valuation.name": "Valuation",
  "hero.layer.valuation.question": "Is the price right?",
  "hero.layer.valuation.fact": "WOZ €11,190 per m² — 54% above the buurt.",
  "hero.layer.valuation.desc": "WOZ per m², comparable sales, plot size, ground lease.",
  "hero.layer.building.name": "Building",
  "hero.layer.building.question": "What shape is it in?",
  "hero.layer.building.fact": "Energy label A, built 1916, 4 floors.",
  "hero.layer.building.desc": "Energy label, construction year, usable area, envelope.",
  "hero.layer.location.name": "Location",
  "hero.layer.location.question": "Is it well placed?",
  "hero.layer.location.fact": "Supermarket 249 m, school 261 m, quiet street.",
  "hero.layer.location.desc": "Transport, schools, noise, air quality, daylight.",
  "hero.layer.neighborhood.name": "Neighborhood",
  "hero.layer.neighborhood.question": "Is the area good?",
  "hero.layer.neighborhood.fact": "Elandsgrachtbuurt — high liveability, 25% owner-occupied.",
  "hero.layer.neighborhood.desc": "Income, tenure mix, safety, liveability trends.",
  "hero.layer.investment.name": "Investment",
  "hero.layer.investment.question": "Will it hold its value?",
  "hero.layer.investment.fact": "Flood risk very low — foundation risk flagged.",
  "hero.layer.investment.desc": "Zoning headroom, flood and foundation risk, growth.",

  "trust.label": "Built on authoritative Dutch data",

  "who.eyebrow": "Who it's for",
  "who.headline": "Four audiences. One platform.",
  "who.intro": "The same intelligence that used to take a week of manual work — done in seconds, in one view.",
  "who.card1.role": "Developers & Investors",
  "who.card1.title": "Know before you bid.",
  "who.card1.desc": "Score every parcel in a target area. Compare across 90+ factors, spot undervalued assets, filter by zoning and development potential. Residential or commercial.",
  "who.card2.role": "Brokers & Advisors",
  "who.card2.title": "Reports in minutes, not weeks.",
  "who.card2.desc": "Generate property intelligence for your clients instantly. Valuation, energy, zoning, risk, demographics — all in one view, ready to present.",
  "who.card3.role": "Homebuyers & Owners",
  "who.card3.title": "Understand any property.",
  "who.card3.desc": "Buying a house? Evaluating your own? Get the full picture — from energy label and noise levels to neighborhood trends and market value.",
  "who.card4.role": "Municipalities & Planners",
  "who.card4.title": "See the whole town.",
  "who.card4.desc": "Per-building insight for entire towns — housing capacity, energy transition, grid constraints, and honest scenario math for your housing programme, on one map.",

  "faq.eyebrow": "Questions",
  "faq.headline": "Things people ask us.",
  // Was "Reach out after you sign up." — an unlinked dead end that put the
  // account before the answer, for people still deciding whether to make one.
  "faq.intro": "Short answers to the objections we hear most. Don't see yours?",
  "faq.contact": "Ask us directly.",
  // Q/A pairs live in ./faqContent.ts — one source for the accordion AND
  // the FAQPage JSON-LD on the homepage (HomeJsonLd).
  ...faqDictEntries(FAQ_EN),

  "signup.eyebrow": "Now live",
  "signup.headline": "Start with three free scores.",
  "signup.intro": "PlotScore is open for everyone. Create an account, score three Dutch properties free, then pick the plan that fits when the search gets serious.",
  "signup.submit": "Create free account",
  "signup.signin": "Sign in",
  "signup.privacy": "No credit card needed. Three free scores on the house.",


  "proof.eyebrow": "This is the score",
  "proof.line": "Six views of one property — swipe through the layers.",
  // Same wording as hero.stage1.cta on purpose: one action, one name for it.
  // (It used to read "Open the app" while pointing at /app — which is
  // auth-walled, so it promised signed-out visitors something it couldn't give.)
  "proof.cta": "Score your first property",
  "proof.caption": "Live from the app — Laurierstraat 218, Amsterdam",

  "carousel.aria": "Six layers of one property — carousel",
  "carousel.pills": "Jump to a layer",
  "carousel.prev": "Previous layer",
  "carousel.next": "Next layer",
  "carousel.overview.label": "Overview",
  "carousel.overview.caption": "Score, layers, confidence — the whole property at a glance.",
  "carousel.overview.alt": "PlotScore overview — composite score 70 'Strong' for Laurierstraat 218-1, Amsterdam: top 18% of Dutch homes, five layer scores and WOZ key figures",
  "carousel.valuation.label": "Valuation",
  "carousel.valuation.caption": "WOZ value, €/m², growth — is the price right?",
  "carousel.valuation.alt": "Valuation layer scoring 95 — €470.000 WOZ value, €11.190 per m², +4% yearly growth and the per-unit price breakdown",
  "carousel.building.label": "Building",
  "carousel.building.caption": "Energy label, year built, usable area — what are you buying?",
  "carousel.building.alt": "Building layer scoring 58 — energy label A, built 1916, solar potential, usable area and sustainability figures",
  "carousel.location.label": "Location",
  "carousel.location.caption": "Amenities, transit, air, noise — how well is it placed?",
  "carousel.location.alt": "Location layer scoring 75 — distances to supermarket, school and transit, plus air quality, noise and green coverage",
  "carousel.neighborhood.label": "Neighborhood",
  "carousel.neighborhood.caption": "Demographics, livability, tenure — who lives around it?",
  "carousel.neighborhood.alt": "Neighborhood layer scoring 60 — livability, crime, education, density and housing tenure for the Elandsgrachtbuurt",
  "carousel.investment.label": "Investment",
  "carousel.investment.caption": "Risk, zoning, value growth — does the case hold up?",
  "carousel.investment.alt": "Investment layer scoring 65 — flood and foundation risk, zoning restrictions, heritage status and value growth",

  "anatomy.eyebrow": "Behind the score",
  "anatomy.headline.a": "Five dimensions.",
  "anatomy.headline.b": "Ninety-plus factors.",
  "anatomy.headline.c": "One number.",
  "anatomy.intro": "That 78 is the surface. Click any card below to see the data beneath it — what makes Keizersgracht 337 score where it does.",
  "anatomy.weight": "WEIGHT",
  "anatomy.factors": "Contributing factors",
  "anatomy.real": "The real thing — live data for an Amsterdam canal house",

  "dim.valuation.name": "Valuation",
  "dim.valuation.insight": "Up 47% over 5 years. 4 comparable transactions within 400m back the valuation. Net yield holds at 4.2% — strong for the canal belt.",
  "dim.building.name": "Building",
  "dim.building.insight": "Rijksmonument with halsgevel facade. Renovated 2019, energy label A+. Limited modification potential.",
  "dim.location.name": "Location",
  "dim.location.insight": "Top-percentile transit access. Quiet for the canal belt. Zero flood risk despite waterside location.",
  "dim.neighborhood.name": "Neighborhood",
  "dim.neighborhood.insight": "Affluent demographics with steady population growth. Tourism pressure offsets some livability gains.",
  "dim.investment.name": "Investment",
  "dim.investment.insight": "Strong rental fundamentals and tight liquidity. Heritage status caps development upside.",
};

const NL: Dict = {
  "seo.h1": "Plot Score (PlotScore) — Vastgoedevaluatie voor huizenkopers in Nederland. Kadaster, WOZ, energielabel en bestemmingsplan voor elk adres.",

  "nav.score": "De score",
  "nav.who": "Voor wie",
  "nav.pricing": "Prijzen",
  "nav.cta": "Begin gratis",
  "nav.menu": "Menu",
  "nav.close": "Sluiten",
  "nav.language": "Taal",

  "hero.stage1.headline": "Eén score voor<br/>elk Nederlands <em>pand.</em>",
  "hero.stage1.body": "Zoek elk adres. 10 miljoen percelen, één samengestelde score, in seconden. Drie gratis scores — geen creditcard nodig.",
  "hero.stage1.cta": "Scoor je eerste pand",
  "hero.stage1.secondary": "Hoe het werkt",
  "hero.stage2.label": "Kies een perceel",
  "hero.stage2.headline": "Klik op een pand.<br/>Welk pand dan ook.",
  "hero.stage2.body": "Waar dan ook in Nederland. Woningen, bedrijfspanden, industrieel — als het een voetafdruk heeft, hebben wij het.",
  "hero.score.label": "Samengestelde score",
  "hero.score.cta": "Scoor je eerste pand",
  "hero.scroll": "Scroll om te ontdekken",

  "hero.split.label": "Anatomie van een score",
  "hero.split.headlineQ": "Vijf vragen.<br/>Eén getal.",
  "hero.split.headline": "Vijf lagen.<br/>Eén getal.",
  "hero.composite.body":
    "90+ gescoorde factoren over vijf lagen, teruggebracht tot één getal.",
  "hero.layer.valuation.name": "Waardering",
  "hero.layer.valuation.question": "Klopt de prijs?",
  "hero.layer.valuation.fact": "WOZ €11.190 per m² — 54% boven de buurt.",
  "hero.layer.valuation.desc": "WOZ per m², vergelijkbare verkopen, perceel, erfpacht.",
  "hero.layer.building.name": "Gebouw",
  "hero.layer.building.question": "Hoe staat het pand erbij?",
  "hero.layer.building.fact": "Energielabel A, bouwjaar 1916, 4 verdiepingen.",
  "hero.layer.building.desc": "Energielabel, bouwjaar, gebruiksoppervlak, envelop.",
  "hero.layer.location.name": "Locatie",
  "hero.layer.location.question": "Ligt het goed?",
  "hero.layer.location.fact": "Supermarkt 249 m, school 261 m, rustige straat.",
  "hero.layer.location.desc": "Bereikbaarheid, scholen, geluid, luchtkwaliteit, daglicht.",
  "hero.layer.neighborhood.name": "Buurt",
  "hero.layer.neighborhood.question": "Is de buurt goed?",
  "hero.layer.neighborhood.fact": "Elandsgrachtbuurt — hoge leefbaarheid, 25% koop.",
  "hero.layer.neighborhood.desc": "Inkomen, eigendomsmix, veiligheid, leefbaarheid.",
  "hero.layer.investment.name": "Investering",
  "hero.layer.investment.question": "Houdt het zijn waarde?",
  "hero.layer.investment.fact": "Overstromingsrisico zeer laag — funderingsrisico gesignaleerd.",
  "hero.layer.investment.desc": "Bestemmingsruimte, overstromings- en funderingsrisico, groei.",

  "trust.label": "Gebouwd op gezaghebbende Nederlandse data",

  "who.eyebrow": "Voor wie",
  "who.headline": "Vier doelgroepen. Eén platform.",
  "who.intro": "Dezelfde intelligentie waar vroeger een week handwerk voor nodig was — in seconden, in één overzicht.",
  "who.card1.role": "Ontwikkelaars & Investeerders",
  "who.card1.title": "Weet het voordat je biedt.",
  "who.card1.desc": "Scoor elk perceel in je doelgebied. Vergelijk op 90+ factoren, herken ondergewaardeerde panden, filter op bestemming en ontwikkelpotentieel. Woningen of commercieel.",
  "who.card2.role": "Makelaars & Adviseurs",
  "who.card2.title": "Rapporten in minuten, niet weken.",
  "who.card2.desc": "Genereer direct vastgoedintelligentie voor je klanten. Waardering, energie, bestemming, risico, demografie — alles in één overzicht, klaar om te presenteren.",
  "who.card3.role": "Kopers & Eigenaren",
  "who.card3.title": "Begrijp elk pand.",
  "who.card3.desc": "Huis kopen? Je eigen huis evalueren? Krijg het volledige beeld — van energielabel en geluidsniveau tot buurttrends en marktwaarde.",
  "who.card4.role": "Gemeenten & Planologen",
  "who.card4.title": "Zie de hele kern.",
  "who.card4.desc": "Inzicht per pand voor complete kernen — woningbouwcapaciteit, energietransitie, netcongestie en eerlijk scenario-rekenwerk voor je woningbouwprogramma, op één kaart.",

  "faq.eyebrow": "Vragen",
  "faq.headline": "Wat mensen ons vragen.",
  "faq.intro": "Korte antwoorden op de bezwaren die we het meest horen. Staat die van jou er niet bij?",
  "faq.contact": "Stel je vraag direct.",
  // Q/A-paren staan in ./faqContent.ts — één bron voor de accordion ÉN de
  // FAQPage JSON-LD op de homepage (HomeJsonLd).
  ...faqDictEntries(FAQ_NL),

  "signup.eyebrow": "Nu live",
  "signup.headline": "Begin met drie gratis scores.",
  "signup.intro": "PlotScore is open voor iedereen. Maak een account, score drie Nederlandse panden gratis, en kies daarna het plan dat past wanneer de zoektocht serieus wordt.",
  "signup.submit": "Account aanmaken",
  "signup.signin": "Inloggen",
  "signup.privacy": "Geen creditcard nodig. Drie gratis scores van het huis.",


  "proof.eyebrow": "Dit is de score",
  "proof.line": "Zes perspectieven op één pand — swipe door de lagen.",
  // Zelfde tekst als hero.stage1.cta — één actie, één naam.
  "proof.cta": "Scoor je eerste pand",
  "proof.caption": "Live uit de app — Laurierstraat 218, Amsterdam",

  "carousel.aria": "Zes lagen van één pand — carrousel",
  "carousel.pills": "Spring naar een laag",
  "carousel.prev": "Vorige laag",
  "carousel.next": "Volgende laag",
  "carousel.overview.label": "Overzicht",
  "carousel.overview.caption": "Score, lagen, betrouwbaarheid — het hele pand in één oogopslag.",
  "carousel.overview.alt": "PlotScore-overzicht — samengestelde score 70 'Strong' voor Laurierstraat 218-1, Amsterdam: top 18% van de NL-woningen, vijf laagscores en WOZ-kerncijfers",
  "carousel.valuation.label": "Waardering",
  "carousel.valuation.caption": "WOZ-waarde, €/m², groei — klopt de prijs?",
  "carousel.valuation.alt": "Waarderingslaag met score 95 — €470.000 WOZ-waarde, €11.190 per m², +4% groei per jaar en de prijsopbouw per unit",
  "carousel.building.label": "Gebouw",
  "carousel.building.caption": "Energielabel, bouwjaar, oppervlakte — wat koop je eigenlijk?",
  "carousel.building.alt": "Gebouwlaag met score 58 — energielabel A, bouwjaar 1916, zonnepotentieel, gebruiksoppervlak en duurzaamheidscijfers",
  "carousel.location.label": "Locatie",
  "carousel.location.caption": "Voorzieningen, OV, lucht, geluid — hoe ligt het erbij?",
  "carousel.location.alt": "Locatielaag met score 75 — afstanden tot supermarkt, school en OV, plus luchtkwaliteit, geluid en groen",
  "carousel.neighborhood.label": "Buurt",
  "carousel.neighborhood.caption": "Demografie, leefbaarheid, huur/koop — wie woont eromheen?",
  "carousel.neighborhood.alt": "Buurtlaag met score 60 — leefbaarheid, criminaliteit, opleiding, dichtheid en huur/koop-verhouding in de Elandsgrachtbuurt",
  "carousel.investment.label": "Investering",
  "carousel.investment.caption": "Risico, bestemming, waardegroei — houdt de businesscase stand?",
  "carousel.investment.alt": "Investeringslaag met score 65 — overstromings- en funderingsrisico, planbeperkingen, monumentstatus en waardegroei",

  "anatomy.eyebrow": "Achter de score",
  "anatomy.headline.a": "Vijf dimensies.",
  "anatomy.headline.b": "Negentig+ factoren.",
  "anatomy.headline.c": "Eén getal.",
  "anatomy.intro": "Die 78 is het oppervlak. Klik op een kaart hieronder en zie de data eronder — wat Keizersgracht 337 maakt tot wat het is.",
  "anatomy.weight": "GEWICHT",
  "anatomy.factors": "Bijdragende factoren",
  "anatomy.real": "Het echte werk — live data voor een Amsterdams grachtenpand",

  "dim.valuation.name": "Waardering",
  "dim.valuation.insight": "47% gestegen over 5 jaar. 4 vergelijkbare transacties binnen 400m onderbouwen de waardering. Netto-rendement houdt stand op 4,2% — sterk voor de grachtengordel.",
  "dim.building.name": "Gebouw",
  "dim.building.insight": "Rijksmonument met halsgevel. Gerenoveerd in 2019, energielabel A+. Beperkte aanpassingsmogelijkheden.",
  "dim.location.name": "Locatie",
  "dim.location.insight": "Top-percentiel OV-bereikbaarheid. Rustig voor de grachtengordel. Geen overstromingsrisico ondanks ligging aan het water.",
  "dim.neighborhood.name": "Buurt",
  "dim.neighborhood.insight": "Welvarende demografie met gestage bevolkingsgroei. Toeristische druk compenseert een deel van de leefbaarheidswinst.",
  "dim.investment.name": "Investering",
  "dim.investment.insight": "Sterke huurmarktfundamenten en krappe liquiditeit. Monumentstatus begrenst ontwikkelpotentieel.",
};

const DICTS: Record<Lang, Dict> = { en: EN, nl: NL };

type Ctx = {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string) => string;
};

const LangContext = createContext<Ctx | null>(null);

export function LanguageProvider({
  children,
  initialLang,
}: {
  children: ReactNode;
  /** When provided, pins the language to this value and skips auto-detection.
      Used by route-based localization (/nl/ pages pass initialLang="nl"). */
  initialLang?: Lang;
}) {
  const [lang, setLangState] = useState<Lang>(initialLang ?? "en");

  useEffect(() => {
    // When the page pins the language via route, respect it and persist.
    if (initialLang) {
      setLangState(initialLang);
      try {
        localStorage.setItem("plotscore-lang", initialLang);
      } catch {}
      return;
    }
    try {
      const stored = localStorage.getItem("plotscore-lang") as Lang | null;
      if (stored === "en" || stored === "nl") {
        setLangState(stored);
        return;
      }
      if (typeof navigator !== "undefined" && navigator.language?.toLowerCase().startsWith("nl")) {
        setLangState("nl");
      }
    } catch {}
  }, [initialLang]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = (l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem("plotscore-lang", l);
    } catch {}
  };

  const t = (key: string): string => {
    const v = DICTS[lang][key];
    if (typeof v === "string") return v;
    const fallback = DICTS.en[key];
    return typeof fallback === "string" ? fallback : key;
  };

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang(): Ctx {
  const ctx = useContext(LangContext);
  if (!ctx) {
    // Safe fallback for any component rendered outside the provider
    return {
      lang: "en",
      setLang: () => {},
      t: (k) => (typeof EN[k] === "string" ? (EN[k] as string) : k),
    };
  }
  return ctx;
}
